import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import type { DateValueType } from "react-tailwindcss-datepicker";
import { DateTime } from "luxon";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import {
  buildLifelongLearningLevelSelectOptions,
  buildPgmLevelSelectOptions
} from "@/features/person/utils/education-program-level-options";
import { GET_EDUCATION_PROGRAM_LEVELS_QUERY } from "./queries";
import {
  CREATE_EDUCATION_PROGRAM_LEVEL_MUTATION,
  CREATE_LIFELONG_LEARNING_PROGRAM_LEVEL_MUTATION,
  educationProgramLevelRefetchQueries,
  type CreateEducationProgramLevelResponse,
  type CreateLifelongLearningProgramLevelResponse,
  type LifelongLearningRefetchQuery
} from "./mutations";

type FormValues = {
  levelCode: string;
  startDate: string;
};

export type EducationProgramLevelCatalog = "PGM" | "LIFELONG_LEARNING";

interface AddEducationProgramLevelModalProps {
  onClose(): void;
  open: boolean;
  pgaId: string;
  catalog?: EducationProgramLevelCatalog;
  refetchQueries?: LifelongLearningRefetchQuery[];
}

function buildDefaultValues(): FormValues {
  return {
    levelCode: "",
    startDate: DateTime.local().toISODate() || ""
  };
}

export default function AddEducationProgramLevelModal({
  onClose,
  open,
  pgaId,
  catalog = "PGM",
  refetchQueries
}: AddEducationProgramLevelModalProps) {
  const {
    data,
    loading: loadingLevels,
    error: levelsError
  } = useQuery(GET_EDUCATION_PROGRAM_LEVELS_QUERY, {
    skip: !open,
    fetchPolicy: "cache-first",
    variables: { catalog }
  });

  const levelOptions = useMemo(() => {
    const levels = data?.educationProgramLevels || [];
    return catalog === "LIFELONG_LEARNING"
      ? buildLifelongLearningLevelSelectOptions(levels)
      : buildPgmLevelSelectOptions(levels);
  }, [catalog, data?.educationProgramLevels]);

  const createLevelMutation =
    catalog === "LIFELONG_LEARNING"
      ? CREATE_LIFELONG_LEARNING_PROGRAM_LEVEL_MUTATION
      : CREATE_EDUCATION_PROGRAM_LEVEL_MUTATION;

  const [createProgramLevel, { loading: saving }] = useMutation<
    CreateEducationProgramLevelResponse | CreateLifelongLearningProgramLevelResponse
  >(createLevelMutation, {
    refetchQueries: refetchQueries ?? educationProgramLevelRefetchQueries(pgaId),
    awaitRefetchQueries: true
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: buildDefaultValues()
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(buildDefaultValues());
  }, [open, reset]);

  const handleClose = () => {
    reset(buildDefaultValues());
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    try {
      const { data: mutationData } = await createProgramLevel({
        variables: {
          input: {
            pgaId,
            levelCode: values.levelCode,
            startDate: values.startDate
          }
        }
      });

      const payload =
        catalog === "LIFELONG_LEARNING"
          ? mutationData && "createLifelongLearningProgramLevel" in mutationData
            ? mutationData.createLifelongLearningProgramLevel
            : undefined
          : mutationData && "createEducationProgramLevel" in mutationData
            ? mutationData.createEducationProgramLevel
            : undefined;

      if (!payload?.success) {
        showErrorSnackbar(payload?.message || "Unable to add education program level.");
        return;
      }

      showSnackbar(payload.message || "Education program level added");
      handleClose();
    } catch {
      showErrorSnackbar("Unable to add education program level.");
    }
  };

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Add Level"
      className="w-[520px] h-fit max-h-[90vh]"
      hasFormElements
      displayOverflow
      actions={
        <div className="flex gap-3">
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit(onSubmit)}
            disabled={saving || loadingLevels || !!levelsError}
            testId="add-education-program-level-submit"
          >
            Add Level
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <Controller
          name="levelCode"
          control={control}
          rules={{ required: "Level is required" }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="Level to Create"
              value={value}
              options={levelOptions}
              onSelect={onChange}
              required
              disabled={loadingLevels || !!levelsError}
              error={!!error || !!levelsError}
              errorText={levelsError ? "Unable to load levels" : error?.message}
              data-testid="add-education-program-level-code"
            />
          )}
        />

        <Controller
          name="startDate"
          control={control}
          rules={{ required: "Start date is required" }}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <DateSelect
              label="Start Date"
              value={
                value
                  ? {
                      startDate: value,
                      endDate: value
                    }
                  : null
              }
              onChange={(dateValue: DateValueType | string | null) => {
                if (typeof dateValue === "string") return;
                const startDate = dateValue?.startDate;
                onChange(typeof startDate === "string" ? startDate : "");
              }}
              onBlur={onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              error={!!error}
              errorMessage={error?.message}
            />
          )}
        />
      </div>
    </Dialog>
  );
}
